import { useEffect, useState, useRef } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { Brain, Shield, Bot, Zap, FileText, Database, Moon, Sun } from "lucide-react";
import { Button } from "../components/ui/button";
import { useTheme } from "./ThemeProvider";
import { getAuthContext, synchronizeBrowserTenantContext } from "../lib/api";
import { oauthStartUrl } from "../lib/authUrls";
import { isSupportTab } from '../lib/supportSession';
import { useNativeSupportBootstrap } from '../lib/nativeSupportBootstrap';

function rememberAuthContext(context) {
  synchronizeBrowserTenantContext(context);
  return context;
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const AUTH_ERROR_MESSAGES = {
  account_link_required: "This email already has an ActionWeave account. Use its original sign-in method.",
  invalid_or_expired_state: "This sign-in request expired. Please try again.",
  microsoft_authorization_failed: "Microsoft sign-in was cancelled or denied.",
  microsoft_authorization_unavailable: "Microsoft sign-in is temporarily unavailable.",
  microsoft_email_missing: "Microsoft did not provide an email address for this account.",
  microsoft_identity_invalid: "Microsoft returned an invalid identity. Please try another account.",
  microsoft_identity_missing: "Microsoft did not return the identity required to sign in.",
  microsoft_login_not_configured: "Microsoft sign-in is not configured yet.",
  microsoft_response_invalid: "Microsoft could not validate this sign-in request. Please try again.",
  microsoft_token_exchange_failed: "Microsoft sign-in could not be completed. Please try again.",
  missing_code_or_state: "The sign-in response was incomplete. Please try again.",
  oauth_not_configured: "This sign-in method is not configured yet.",
};

export function LoginPage() {
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme, setTheme } = useTheme();

  useEffect(() => {
    getAuthContext().then(res => {
      const context = rememberAuthContext(res.data);
      if (context?.user?.user_id) navigate("/", { replace: true });
      else setChecking(false);
    }).catch(() => setChecking(false));
  }, [navigate]);

  const handleLogin = (provider) => {
    window.location.href = oauthStartUrl(provider);
  };

  const authError = new URLSearchParams(location.search).get("error");
  const authErrorMessage = authError
    ? AUTH_ERROR_MESSAGES[authError] || "Sign-in could not be completed. Please try again."
    : null;

  if (checking) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-pulse text-primary"><Brain size={48} /></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6" data-testid="login-page">
      <Button
        variant="outline"
        size="icon"
        className="absolute right-5 top-5"
        onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
        aria-label={resolvedTheme === "dark" ? "Use light mode" : "Use dark mode"}
      >
        {resolvedTheme === "dark" ? <Sun /> : <Moon />}
      </Button>
      <div className="max-w-sm w-full space-y-8 text-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            ActionWeave<span className="text-primary">.ai</span>
          </h1>
          <p className="text-muted-foreground text-sm mt-2">Intelligent Workflow Orchestration</p>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {[
            { icon: Brain, label: "LLM Orchestrator", color: "text-primary", surface: "bg-brand-subtle" },
            { icon: Bot, label: "13 Agents", color: "text-category-violet-foreground", surface: "bg-category-violet-surface" },
            { icon: Shield, label: "Guardrails", color: "text-warning-foreground", surface: "bg-warning-surface" },
            { icon: FileText, label: "Documents", color: "text-category-pink-foreground", surface: "bg-category-pink-surface" },
            { icon: Database, label: "Entity Cache", color: "text-category-teal-foreground", surface: "bg-category-teal-surface" },
            { icon: Zap, label: "1,324 Skills", color: "text-success-foreground", surface: "bg-success-surface" },
          ].map((f, i) => (
            <div key={i} className="rounded-lg border border-border bg-card p-3">
              <div className={`mx-auto mb-1.5 flex h-7 w-7 items-center justify-center rounded-md ${f.surface}`}>
                <f.icon className={`h-4 w-4 ${f.color}`} />
              </div>
              <p className="text-[9px] text-muted-foreground">{f.label}</p>
            </div>
          ))}
        </div>

        {authErrorMessage && (
          <div role="alert" className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-left text-xs text-destructive">
            {authErrorMessage}
          </div>
        )}

        <div className="space-y-3">
          <Button onClick={() => handleLogin("google")} size="lg" className="w-full font-medium text-sm py-5" data-testid="login-btn">
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Sign in with Google
          </Button>

          <Button onClick={() => handleLogin("microsoft")} variant="outline" size="lg" className="w-full font-medium text-sm py-5" data-testid="microsoft-login-btn">
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="currentColor" d="M2 2h9.5v9.5H2z" />
              <path fill="currentColor" d="M12.5 2H22v9.5h-9.5z" />
              <path fill="currentColor" d="M2 12.5h9.5V22H2z" />
              <path fill="currentColor" d="M12.5 12.5H22V22h-9.5z" />
            </svg>
            Sign in with Microsoft
          </Button>
        </div>

        <p className="text-[10px] text-muted-foreground">Use your Google or Microsoft account</p>
      </div>
    </div>
  );
}

/** After OAuth, backend redirects to /#session_token=… — store token and enter the app. */
export function OAuthSessionHandler() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hashParams = new URLSearchParams(location.hash.replace("#", "?"));
    const token = hashParams.get("session_token");
    if (token) {
      localStorage.setItem("session_token", token);
      navigate("/", { replace: true });
      window.history.replaceState(null, "", location.pathname + location.search);
    } else {
      navigate("/login", { replace: true });
    }
  }, [location, navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <Brain className="h-10 w-10 text-primary animate-pulse mx-auto mb-3" />
        <p className="text-muted-foreground text-sm">Finishing sign-in…</p>
      </div>
    </div>
  );
}

export function ProtectedRoute({ children }) {
  const supportBootstrap = useNativeSupportBootstrap();
  const [authState, setAuthState] = useState(supportBootstrap?.auth ? true : null); // null=checking, true, false
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const entryLocation = useRef(location.key);
  const entryAuthorization = useRef(supportBootstrap?.auth);

  useEffect(() => {
    if (entryAuthorization.current && location.key === entryLocation.current) {
      // The parent has just verified this session before mounting us. Do not
      // serialize an identical check in front of the first page. Navigation
      // still rechecks identity, including a return to the original entry.
      setAuthState(true);
      return;
    }
    entryAuthorization.current = null;
    let current = true;
    if (location.state?.user && !isSupportTab()) {
      setUser(location.state.user);
      setAuthState(true);
      return;
    }

    getAuthContext().then(res => {
      if (!current) return;
      const context = rememberAuthContext(res.data);
      if (context?.user?.user_id) {
        setUser(context.user);
        setAuthState(true);
      } else {
        setAuthState(false);
      }
    }).catch(() => {
      if (current) setAuthState(false);
    });
    return () => { current = false; };
  }, [location.key, location.pathname, location.state, navigate]);

  if (authState === null) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Brain className="h-8 w-8 text-primary animate-pulse" />
    </div>
  );

  if (authState === false) {
    if (isSupportTab()) return <p role="alert">Support authorization is unavailable or expired. Open a new session from Support.</p>;
    return <Navigate to="/login" replace />;
  }

  return children;
}
